import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Content,
  EmptyState,
  Header,
  Link,
  MarkdownContent,
  Page,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Chip, Divider, Grid, Typography, makeStyles } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { useCachedPluginSummary, usePluginDetail, useSourceUrl } from './api';
import type { HooksFile, PluginItem } from './api';

/**
 * One plugin: what it holds, and what each piece does.
 *
 * The descriptions come from the items' own frontmatter, which is the same text
 * Claude Code matches against when deciding whether to invoke a skill — so what
 * is on screen is what actually drives behaviour, not a hand-maintained gloss.
 */

const useStyles = makeStyles(theme => ({
  section: { marginTop: 28 },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    marginBottom: 12,
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 650 },
  sectionCount: {
    color: 'var(--ar-fg-3)',
    fontSize: '0.8125rem',
  },
  item: {
    display: 'block',
    padding: '12px 14px',
    height: '100%',
    border: '1px solid var(--ar-border-soft)',
    borderRadius: 8,
    textDecoration: 'none',
    transition: 'border-color 120ms ease',
    '&:hover': { borderColor: 'var(--ar-accent)' },
  },
  itemName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  },
  itemDesc: {
    marginTop: 5,
    color: 'var(--ar-fg-2)',
    fontSize: '0.8125rem',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    padding: '14px 0',
    fontSize: '0.8125rem',
  },
  metaItem: { display: 'flex', gap: 8 },
  metaLabel: { color: 'var(--ar-fg-3)' },
  keywords: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  // Hooks are configuration, not prose — shown as the event map they are.
  hookEvent: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: 12,
    padding: '10px 0',
    borderTop: '1px solid var(--ar-border-soft)',
    fontSize: '0.8125rem',
  },
  hookName: {
    fontWeight: 600,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  },
  hookCmd: {
    color: 'var(--ar-fg-2)',
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    wordBreak: 'break-all',
  },
  matcher: { color: 'var(--ar-fg-3)' },
  readme: { marginTop: 8 },
}));

const ItemGrid = ({
  items,
  descriptions,
  base,
}: {
  items: PluginItem[];
  /** Absent while the detail loads: names are known, descriptions are not. */
  descriptions?: Record<string, string>;
  base: string;
}) => {
  const classes = useStyles();
  return (
    <Grid container spacing={2}>
      {items.map(item => (
        <Grid item xs={12} sm={6} md={4} key={item.path}>
          <RouterLink
            className={classes.item}
            to={`${base}/${item.kind}/${item.name}`}
          >
            <div className={classes.itemName}>{item.name}</div>
            {descriptions ? (
              descriptions[item.path] && (
                <div className={classes.itemDesc}>
                  {descriptions[item.path]}
                </div>
              )
            ) : (
              <>
                <Skeleton variant="text" />
                <Skeleton variant="text" width="60%" />
              </>
            )}
          </RouterLink>
        </Grid>
      ))}
    </Grid>
  );
};

const Hooks = ({ hooks }: { hooks: HooksFile }) => {
  const classes = useStyles();
  return (
    <>
      {hooks.description && (
        <Typography className={classes.itemDesc} style={{ marginBottom: 6 }}>
          {hooks.description}
        </Typography>
      )}
      {Object.entries(hooks.hooks ?? {}).map(([event, matchers]) => (
        <div className={classes.hookEvent} key={event}>
          <div>
            <div className={classes.hookName}>{event}</div>
            {matchers.some(m => m.matcher) && (
              <div className={classes.matcher}>
                {matchers
                  .map(m => m.matcher)
                  .filter(Boolean)
                  .join(', ')}
              </div>
            )}
          </div>
          <div>
            {matchers
              .flatMap(m => m.hooks ?? [])
              .map(h => (
                <div className={classes.hookCmd} key={h.command}>
                  {/* The plugin-root variable is noise to a reader. */}
                  {h.command.replace('${CLAUDE_PLUGIN_ROOT}/', '')}
                </div>
              ))}
          </div>
        </div>
      ))}
    </>
  );
};

export const PluginPage = () => {
  const classes = useStyles();
  const { pluginName = '' } = useParams();
  const { detail, error, notFound, loading } = usePluginDetail(pluginName);
  // Arriving from the browse page, the index already holds this plugin's
  // name, description and item list — render those at once, and let only the
  // descriptions, hooks and README wait for the detail.
  const summary = useCachedPluginSummary(pluginName);
  const plugin = detail?.plugin ?? summary;
  const pluginSourceUrl = useSourceUrl(
    plugin ? `plugins/${plugin.dir}` : undefined,
  );

  if (notFound) {
    return (
      <Page themeId="home">
        <Header title={pluginName} />
        <Content>
          <EmptyState
            missing="content"
            title="No such plugin"
            description={`The marketplace has no plugin named "${pluginName}".`}
          />
        </Content>
      </Page>
    );
  }

  const base = `/marketplace/${pluginName}`;
  const { skills = [], agents = [], commands = [] } = plugin?.contents ?? {};

  return (
    <Page themeId="home">
      <Header
        title={pluginName}
        subtitle={detail?.manifest?.description ?? plugin?.description}
      />
      <Content>
        {error && <ResponseErrorPanel error={error} />}

        {loading && !plugin && (
          <div data-testid="plugin-skeleton">
            <Skeleton variant="text" width="40%" height={32} />
            <Grid container spacing={2} className={classes.section}>
              {Array.from({ length: 6 }, (_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rect" height={84} />
                </Grid>
              ))}
            </Grid>
          </div>
        )}

        {plugin && (
          <>
            <div className={classes.meta}>
              {(detail?.manifest?.version ?? plugin.version) && (
                <span className={classes.metaItem}>
                  <span className={classes.metaLabel}>Version</span>
                  <span>{detail?.manifest?.version ?? plugin.version}</span>
                </span>
              )}
              {(detail?.manifest?.author?.name ?? plugin.author?.name) && (
                <span className={classes.metaItem}>
                  <span className={classes.metaLabel}>Author</span>
                  <span>
                    {detail?.manifest?.author?.name ?? plugin.author?.name}
                  </span>
                </span>
              )}
              {plugin.category && (
                <span className={classes.metaItem}>
                  <span className={classes.metaLabel}>Category</span>
                  <span>{plugin.category}</span>
                </span>
              )}
              {detail?.manifest?.license && (
                <span className={classes.metaItem}>
                  <span className={classes.metaLabel}>License</span>
                  <span>{detail.manifest.license}</span>
                </span>
              )}
              <span className={classes.metaItem}>
                <Link to={pluginSourceUrl}>Source</Link>
              </span>
            </div>

            {(plugin.keywords ?? []).length > 0 && (
              <div className={classes.keywords}>
                {(plugin.keywords ?? []).map(k => (
                  <Chip key={k} size="small" label={k} />
                ))}
              </div>
            )}

            {skills.length > 0 && (
              <div className={classes.section}>
                <div className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    Skills
                  </Typography>
                  <span className={classes.sectionCount}>
                    invoked by Claude when the description matches the task
                  </span>
                </div>
                <ItemGrid
                  items={skills}
                  descriptions={
                    detail?.descriptions ?? (error ? {} : undefined)
                  }
                  base={base}
                />
              </div>
            )}

            {agents.length > 0 && (
              <div className={classes.section}>
                <div className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    Agents
                  </Typography>
                  <span className={classes.sectionCount}>
                    subagents this plugin makes available
                  </span>
                </div>
                <ItemGrid
                  items={agents}
                  descriptions={
                    detail?.descriptions ?? (error ? {} : undefined)
                  }
                  base={base}
                />
              </div>
            )}

            {commands.length > 0 && (
              <div className={classes.section}>
                <div className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    Commands
                  </Typography>
                  <span className={classes.sectionCount}>slash commands</span>
                </div>
                <ItemGrid
                  items={commands}
                  descriptions={
                    detail?.descriptions ?? (error ? {} : undefined)
                  }
                  base={base}
                />
              </div>
            )}

            {detail?.hooks && (
              <div className={classes.section}>
                <div className={classes.sectionHead}>
                  <Typography className={classes.sectionTitle}>
                    Hooks
                  </Typography>
                  <span className={classes.sectionCount}>
                    run automatically at these events
                  </span>
                </div>
                <Hooks hooks={detail.hooks} />
              </div>
            )}

            {/* Not after an error: a skeleton that never resolves reads as
                still loading, beside the panel saying it failed. */}
            {!detail && !error && plugin.contents.hasReadme && (
              <div className={classes.section}>
                <Skeleton variant="rect" height={160} />
              </div>
            )}

            {detail?.readme && (
              <div className={classes.section}>
                <Divider />
                <div className={classes.readme}>
                  {/* Frontmatter is stripped server-side. */}
                  <MarkdownContent
                    content={detail.readme}
                    linkTarget="_blank"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Content>
    </Page>
  );
};
